import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import httpService  from '../../api/httpService';
import { useAuth } from '../../contexts/AuthContext';

function Organization() {
    const navigate = useNavigate();
    const { userDetails } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // userId from localStorage (via AuthContext) – API uses it to get role and return all orgs (admin) or only user's org (non-admin)
    const userId = userDetails?.userid ?? null;
    const isAdmin = userDetails?.admin === true || userDetails?.admin === 1;

// <----- fetching details for organization page ----->   
    const fetchData = async (endpoint = '/Organization', searchParams = {}, pageNumber = 1) => {
        setLoading(true);
        try{
            const params = {
                pageNumber: pageNumber,
                pageSize: 10,
                ...searchParams
            };
            if (userId != null) params.userId = userId;
            const result = await httpService.getData(endpoint, params, true);
            setData(result);
            setCurrentPage(result.retObject.currentPage);
            setTotalPages(result.retObject.totalPages);
        }catch(err){
            console.log(err.message);
            setError(err.message);
        }finally {
            setLoading(false);
        }
    };

    useEffect(() =>{
        fetchData();
    },[userId]);

    // <------- Logic for pagination ------->  
    const handleNext = () => {
        if (currentPage < totalPages) {
            fetchData("/Organization", {}, currentPage + 1);
        }
    };

    const handleFirst = () => {
        if (currentPage > 1) {
            fetchData("/Organization", {}, 1);
        }
    };

    const handleLast = () => {
        if (currentPage < totalPages) {
            fetchData("/Organization", {}, totalPages);
        }
    };

    const handlePrevious = () => {
        if (currentPage > 1) {
            fetchData("/Organization", {}, currentPage - 1);
        }
    };

    return (
        <div className="app-content p-4">
            <div className="container-fluid mt-4">
                <div className="card custom-card create-user-card">
                    <div className="card-header justify-content-between">
                        <div className="card-title">Registry Info</div>
                        <ul className='custom_btns'>
                            {isAdmin && (
                            <li>
                                <Link to="/add-organization-info" type="button" className="btn btn-outline-secondary btn-sm btn-wave">
                                    <i className="fas fa-plus fa-sm side-menu__icon me-1 align-middle d-inline-block"></i>Add New Registry
                                </Link>
                            </li>
                            )}
                        </ul>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center loader-container">
                                <button className="btn btn-outline-secondary mt-3" type="button">
                                    <span className="spinner-border spinner-border-sm align-middle me-2" role="status"
                                        aria-hidden="true"></span>
                                    Loading...
                                </button>
                            </div>
                        ):( 
                            <>
                                <div className='row pagination'>
                                    <div className='col-md-12 text-end mb-3'>                                              
                                        <span> Page {currentPage} of {totalPages} </span>                                              
                                        <span className='btn btn-icon btn-sm bg-pagi-transparent'
                                            onClick={handleFirst} 
                                            disabled={currentPage === totalPages}
                                        >
                                                <i className="fas fa-angles-left"></i>
                                        </span>
                                        <span className='btn btn-icon btn-sm bg-pagi-transparent'
                                            onClick={handlePrevious} 
                                            disabled={currentPage === 1}                                                   
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </span>                                                
                                        <span className='btn btn-icon btn-sm bg-pagi-transparent'
                                            onClick={handleNext} 
                                            disabled={currentPage === totalPages}
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                        </span>
                                        <span className='btn btn-icon btn-sm bg-pagi-transparent'
                                            onClick={handleLast} 
                                            disabled={currentPage === totalPages}
                                            >
                                                <i className="fas fa-angles-right"></i>
                                        </span>                                               
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table id="dataTable" className='table table-hover border table-bordered'>
                                        <thead>
                                            <tr>
                                                <th>Registry Name</th>
                                                <th>Primary Contact</th>
                                                <th>ISAKOS Member Contact</th>
                                                <th>Email</th>
                                                <th>Address</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.retObject?.orgs?.length > 0 ? (data.retObject.orgs.map((item, index) => (
                                                <tr key = {item.orgid}>
                                                    <td>{item.orgname}</td>
                                                    <td>{item.primconname}</td>
                                                    <td>{item.secconname}</td>
                                                    <td>{item.primconemail}</td>
                                                    {/* <td>{`${item.address1 || ""}, ${item.state || ""}, ${item.countryRef.countryName || ""}`.trim()}</td> */}
                                                    <td>
                                                        {[item.address1, item.state, item.countryRef?.countryName]
                                                        .filter(Boolean)
                                                        .join(", ")}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${Number(item.status ?? item.Status) === 1 ? 'bg-success' : 'bg-danger'}`}>
                                                            {Number(item.status ?? item.Status) === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {isAdmin ? (
                                                            <span className="action-icon btn-icon btn-sm btn-success-light me-3" onClick={() => navigate(`/edit-organization-info/${item.orgid}`)} title="Edit">
                                                                <i className="fa-regular fa-pen-to-square"></i>
                                                            </span>
                                                        ) : (
                                                            <span className="action-icon btn-icon btn-sm btn-info-light me-3" onClick={() => navigate(`/view-organization-info/${item.orgid}`)} title="View details">
                                                                <i className="fa-regular fa-eye"></i>
                                                            </span>
                                                        )}
                                                    </td>                  
                                                </tr>
                                            ))):(
                                                <tr>
                                                    <td colSpan="7" className="text-center">
                                                        No Data
                                                    </td>
                                                </tr>
                                            )} 
                                        </tbody>
                                        {/* <tbody>
                                            <tr>
                                                <td>Abcdeffg</td>
                                                <td>4</td>
                                                <td>xyz</td>
                                                <th>abcdeffg@gmail.com</th>
                                                <th>Copenhagen, Denmark</th>
                                                <td>
                                                    <span className="action-icon btn-icon btn-sm btn-success-light me-3"onClick={() => navigate('/edit-organization-info')} >
                                                        <i className="fa-regular fa-pen-to-square"></i>
                                                    </span>
                                                    <span className="action-icon btn-icon btn-sm btn-danger-light">
                                                        <i className="fa-regular fa-trash-can"></i>
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Abcdeffg</td>
                                                <td>4</td>
                                                <td>xyz</td>
                                                <th>abcdeffg@gmail.com</th>
                                                <th>Paris, France</th>
                                                <td>
                                                    <span className="action-icon btn-icon btn-sm btn-success-light me-3"onClick={() => navigate('/settings')} >
                                                        <i className="fa-regular fa-pen-to-square"></i>
                                                    </span>
                                                    <span className="action-icon btn-icon btn-sm btn-danger-light">
                                                        <i className="fa-regular fa-trash-can"></i>
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Abcdeffg</td>
                                                <td>4</td>
                                                <td>xyz</td>
                                                <th>abcdeffg@gmail.com</th>
                                                <th>Oslo, Norway</th>
                                                <td>
                                                    <span className="action-icon btn-icon btn-sm btn-success-light me-3"onClick={() => navigate('/settings')} >
                                                        <i className="fa-regular fa-pen-to-square"></i>
                                                    </span>
                                                    <span className="action-icon btn-icon btn-sm btn-danger-light">
                                                        <i className="fa-regular fa-trash-can"></i>
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Abcdeffg</td>
                                                <td>4</td>
                                                <td>xyz</td>
                                                <th>abcdeffg@gmail.com</th>
                                                <th>Berlin, Germany</th>
                                                <td>
                                                    <span className="action-icon btn-icon btn-sm btn-success-light me-3"onClick={() => navigate('/settings')} >
                                                        <i className="fa-regular fa-pen-to-square"></i>
                                                    </span>
                                                    <span className="action-icon btn-icon btn-sm btn-danger-light">
                                                        <i className="fa-regular fa-trash-can"></i>
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Abcdeffg</td>
                                                <td>4</td>
                                                <td>xyz</td>
                                                <th>abcdeffg@gmail.com</th>
                                                <th>Differdange, Luxembourg</th>
                                                <td>
                                                    <span className="action-icon btn-icon btn-sm btn-success-light me-3" onClick={() => navigate('/settings')} >
                                                        <i className="fa-regular fa-pen-to-square"></i>
                                                    </span>
                                                    <span className="action-icon btn-icon btn-sm btn-danger-light">
                                                        <i className="fa-regular fa-trash-can"></i>
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody> */}
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>     
        </div>
    )
}

export default Organization;
